import React, { useEffect, useState } from "react";
import api from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const blank = { name: "", username: "", password: "", role: "staff" };

export default function Users() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);

  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.username || !form.password || !form.name) { toast.error("All fields required"); return; }
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false); setForm(blank); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3 animate-fade-in" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-heading text-2xl md:text-3xl">User Management</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#B86F77] hover:bg-[#A35D65] h-9" data-testid="add-user-btn"><Plus className="h-4 w-4 mr-1" /> Add User</Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-8" data-testid="user-name" /></div>
              <div><Label className="text-xs">Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="h-8" data-testid="user-username" /></div>
              <div><Label className="text-xs">Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-8" data-testid="user-password" /></div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="h-8" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="h-8 border-[#E8E3DF]" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="h-8 bg-[#B86F77] hover:bg-[#A35D65]" onClick={save} data-testid="user-save">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-[#E8E3DF] overflow-hidden">
        <Table className="tps-table">
          <TableHeader className="bg-[#FCF9F9]">
            <TableRow>
              <TableHead className="py-2">Name</TableHead>
              <TableHead>Username</TableHead>
              <TableHead>Role</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.username}`}>
                <TableCell className="py-2 font-medium">{u.name}</TableCell>
                <TableCell className="py-2 text-sm">@{u.username}</TableCell>
                <TableCell className="py-2 text-sm capitalize">{u.role}</TableCell>
                <TableCell className="py-2 text-right">
                  <button onClick={() => remove(u.id)} className="text-rose-600 hover:bg-rose-50 p-1 rounded" data-testid={`user-del-${u.username}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
